import { NavLink } from "react-router-dom";
import type { CSSProperties, ReactNode } from "react";
import type { Theme } from "../../theme/useTheme";
import { formatAgo } from "../../utils/format";
import type { BotStatus } from "../../api/types";
import { CrabLogo } from "../CrabLogo/CrabLogo";

interface NavSidebarProps {
  errCount: number;
  botStatus: BotStatus | undefined;
  theme: Theme;
  onToggleTheme: () => void;
  isAdmin: boolean;
  userDisplayName: string | undefined;
  onLogout: () => void;
}

const navButtonBase: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 9,
  flex: "none",
  padding: "9px 12px",
  border: 0,
  borderRadius: 10,
  font: "600 14px 'Golos Text',sans-serif",
  cursor: "pointer",
  textAlign: "left",
  whiteSpace: "nowrap",
};

function NavItem({ to, children }: { to: string; children: ReactNode }) {
  return (
    <NavLink
      to={to}
      style={({ isActive }) => ({
        ...navButtonBase,
        background: isActive ? "var(--surface2)" : "transparent",
        color: isActive ? "var(--accent)" : "var(--fg2)",
        textDecoration: "none",
      })}
    >
      {children}
    </NavLink>
  );
}

// Matches the prototype's <aside> — logo, nav (Обзор/Сделки/Логи/Управление,
// + admin-only Пользователи which the prototype doesn't have), bot-status
// footer, theme toggle. Responsive collapse to a top bar is done via CSS
// (styles/tokens.css doesn't define it — see the App-level media query in
// AppShell.tsx) rather than the prototype's JS-measured breakpoint.
export function NavSidebar({ errCount, botStatus, theme, onToggleTheme, isAdmin, userDisplayName, onLogout }: NavSidebarProps) {
  return (
    <aside
      style={{
        flex: "none",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 18,
        width: 240,
        padding: "22px 16px",
        background: "var(--surface)",
        borderRight: "1px solid var(--line)",
        position: "sticky",
        top: 0,
        height: "100vh",
        zIndex: 40,
      }}
      className="nav-sidebar"
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "2px 4px", flex: "none" }} title="Crypto Alt Bots">
        <span
          style={{
            width: 26,
            height: 26,
            flex: "none",
            borderRadius: 8,
            background: "var(--accent)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CrabLogo size={16} color="var(--onfill)" />
        </span>
        <span className="nav-brand-text" style={{ display: "flex", flexDirection: "column", lineHeight: 1.15 }}>
          <span style={{ font: "700 13px 'Golos Text',sans-serif", letterSpacing: ".09em" }}>CrABs</span>
          <span style={{ font: "500 11px 'Golos Text',sans-serif", color: "var(--fg3)", letterSpacing: ".02em" }}>Trading Bot</span>
        </span>
      </div>

      <nav style={{ display: "flex", flexDirection: "column", gap: 2, width: "100%", marginTop: 6 }}>
        <NavItem to="/">Обзор</NavItem>
        <NavItem to="/trades">Сделки</NavItem>
        <NavItem to="/logs">
          Логи{" "}
          <span
            style={{
              font: "500 10.5px 'JetBrains Mono',monospace",
              padding: "1px 5px",
              borderRadius: 5,
              background: "var(--shortsoft)",
              color: "var(--short)",
            }}
          >
            {errCount}
          </span>
        </NavItem>
        <NavItem to="/control">Управление</NavItem>
        {isAdmin && <NavItem to="/users">Пользователи</NavItem>}
      </nav>

      <div className="nav-side-foot" style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: "auto", width: "100%" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 7, padding: "15px 2px 2px", borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
            <span
              style={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: botStatus?.running ? "var(--long)" : "var(--short)",
              }}
            />
            <span style={{ font: "600 11.5px 'Golos Text',sans-serif", color: "var(--fg2)", letterSpacing: ".04em" }}>
              {/* With more than one bot instance, a plain "работает"/
                  "остановлен" binary would hide a partial outage (2 of 3
                  alive still reading as fine) — show the count instead so
                  one dead instance among several doesn't get averaged away
                  into a healthy-looking status. */}
              {botStatus && botStatus.total_count > 1
                ? `БОТЫ ${botStatus.active_count}/${botStatus.total_count}`
                : botStatus?.running
                  ? "БОТ РАБОТАЕТ"
                  : "БОТ ОСТАНОВЛЕН"}
            </span>
          </div>
          <div style={{ font: "400 11px 'JetBrains Mono',monospace", color: "var(--fg3)" }}>
            обновлено {botStatus ? formatAgo(botStatus.last_update_at) : "—"} назад
          </div>
        </div>
        {userDisplayName && (
          <div style={{ font: "400 11.5px 'Golos Text',sans-serif", color: "var(--fg3)", padding: "0 2px" }}>{userDisplayName}</div>
        )}
        <button
          type="button"
          onClick={onToggleTheme}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: "8px 11px",
            border: "1px solid var(--line)",
            borderRadius: 9,
            background: "transparent",
            color: "var(--fg2)",
            font: "500 12.5px 'Golos Text',sans-serif",
            cursor: "pointer",
          }}
        >
          <span>Тема</span>
          <span style={{ font: "600 11px 'JetBrains Mono',monospace", color: "var(--fg)" }}>{theme === "dark" ? "DARK" : "LIGHT"}</span>
        </button>
        <button
          type="button"
          onClick={onLogout}
          style={{
            padding: "8px 11px",
            border: "1px solid var(--line)",
            borderRadius: 9,
            background: "transparent",
            color: "var(--fg2)",
            font: "500 12.5px 'Golos Text',sans-serif",
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          Выйти
        </button>
      </div>
    </aside>
  );
}
